import { useState, MouseEvent } from 'react';
import {
  ListItem,
  ListItemIcon,
  Typography,
  Box,
  makeStyles,
  createStyles,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  CircularProgress,
} from '@material-ui/core';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import HourglassEmptyIcon from '@material-ui/icons/HourglassEmpty';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import { Draggable } from 'react-beautiful-dnd';

import { CampaignProps } from 'src/types/campaign';

import { ReactComponent as CampaignsIcon } from 'src/assets/icons/campaigns.svg';

export const useStyles = makeStyles(() =>
  createStyles({
    container: {
      padding: 0,
      alignItems: 'flex-start',
    },
    campaignContent: {
      display: 'flex',
      alignItems: 'center',
      gap: 8,
      flex: 1,
      minWidth: 0,
    },
    iconContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: 4,
      flexShrink: 0,
    },
    statusIcon: {
      fontSize: 16,
    },
    scoringProgress: {
      color: '#5EA1F2',
    },
  })
);

interface CampaignItemProps {
  campaign: CampaignProps;
  index: number;
  noDraggable?: boolean;
  showActions?: boolean;
  onSelectCampaign: (campaignId: string) => void;
  onEditCampaign?: (campaign: CampaignProps) => void;
  onDeleteCampaign?: (campaign: CampaignProps) => void;
}

export const CampaignItem = ({
  campaign,
  index,
  noDraggable = false,
  showActions = false,
  onSelectCampaign,
  onEditCampaign,
  onDeleteCampaign,
}: CampaignItemProps) => {
  const classes = useStyles();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const openMenu = Boolean(anchorEl);

  const handleOpenMenu = (event: MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    event.preventDefault();
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleEdit = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onEditCampaign?.(campaign);
  };

  const handleDelete = (event: MouseEvent) => {
    event.stopPropagation();
    handleCloseMenu();
    onDeleteCampaign?.(campaign);
  };

  const actionsMenu = showActions ? (
    <div onClick={(e) => e.stopPropagation()}>
      <IconButton
        onClick={handleOpenMenu}
        style={{ padding: 2, marginLeft: 4 }}
        size='small'
      >
        <MoreVertIcon htmlColor='#757575' style={{ fontSize: 16 }} />
      </IconButton>
      <Menu
        id={`campaign-item-menu-${campaign.id}`}
        anchorEl={anchorEl}
        open={openMenu}
        onClose={handleCloseMenu}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={handleEdit}>Edit</MenuItem>
        <MenuItem onClick={handleDelete} style={{ color: '#ff0000' }}>
          Delete
        </MenuItem>
      </Menu>
    </div>
  ) : null;

  const row = (
    <>
      <ListItemIcon style={{ minWidth: 30 }}>
        <CampaignsIcon
          stroke='#757575'
          fill='none'
          strokeWidth={1}
          strokeLinecap='round'
          strokeLinejoin='round'
          style={{ width: 16, height: 16 }}
        />
      </ListItemIcon>
      <Box className={classes.campaignContent}>
        <Tooltip title={campaign.name} placement='top'>
          <Typography
            style={{
              fontSize: 14,
              fontWeight: 400,
              lineHeight: '18px',
              flex: 1,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {campaign.name}
          </Typography>
        </Tooltip>
        <Box className={classes.iconContainer}>
          {campaign.scoringStatus === 'IN_PROGRESS' && (
            <Tooltip title='Scoring...' placement='top'>
              <CircularProgress
                size={12}
                thickness={5}
                className={classes.scoringProgress}
              />
            </Tooltip>
          )}
          <Typography
            style={{
              fontSize: 11,
              fontWeight: 400,
              color: '#667085',
              marginRight: 8,
            }}
          >
            {campaign.alignmentRequired === false ||
            campaign.alignmentRequired === undefined
              ? 'S'
              : 'S&E'}
          </Typography>
          {campaign.status === 'completed' ? (
            <CheckCircleIcon
              className={classes.statusIcon}
              style={{ color: '#10B981' }}
              titleAccess='Complete'
            />
          ) : (
            <HourglassEmptyIcon
              className={classes.statusIcon}
              style={{ color: '#F59E0B' }}
              titleAccess={
                campaign.status === 'pending'
                  ? 'Pending'
                  : campaign.status === 'failed'
                  ? 'Failed'
                  : 'Processing'
              }
            />
          )}
          {actionsMenu}
        </Box>
      </Box>
    </>
  );

  if (noDraggable) {
    return (
      <ListItem
        button
        className={classes.container}
        onClick={() => {
          onSelectCampaign(campaign.id);
        }}
      >
        {row}
      </ListItem>
    );
  }

  return (
    <Draggable draggableId={campaign.id} index={index}>
      {(provided) => (
        <ListItem
          button
          className={classes.container}
          onClick={() => {
            onSelectCampaign(campaign.id);
          }}
          {...provided.draggableProps}
          {...provided.dragHandleProps}
          ref={provided.innerRef}
        >
          {row}
        </ListItem>
      )}
    </Draggable>
  );
};
